import { CheckIcon, PlusIcon, XIcon } from "lucide-react";
import { toast } from "react-toastify";

import type { Project } from "@tudigo-monorepo/core-tudigo-api-models";
import { useGetProjectsQuery } from "@tudigo-monorepo/web-tudigo-api-client";
import { useComparator } from "@tudigo-monorepo/web-tudigo-comparator";
import {
  AlertRebrand,
  AlertRebrandTitle,
  ButtonRebrand,
} from "@tudigo-monorepo/web-tudigo-components";
import { UI_PROJECTS_COMPARATOR_MODAL_PROJECT_ADDED } from "@tudigo-monorepo/web-tudigo-tracking";

import {
  ProjectCard,
  ProjectCardAmount,
  ProjectCardAvatar,
  ProjectCardBadges,
  ProjectCardClubAvatar,
  ProjectCardContent,
  ProjectCardCover,
  ProjectCardDescription,
  ProjectCardFooter,
  ProjectCardHeader,
  ProjectCardImage,
  ProjectCardImageFallback,
  ProjectCardLabel,
  ProjectCardPercentage,
  ProjectCardStatusBadge,
} from "../../../../../shared-external/components/project-card/project-card";
import { useProjectsComparatorCustomerTracking } from "../../../hooks/use-projects-comparator-customer-tracking";

export function ProjectsComparatorSuggestionBlock() {
  const { config, selectionCount, selectItem, isSelected } =
    useComparator<Project>();

  const { customerTrackComparator } = useProjectsComparatorCustomerTracking();

  const { data: startedProjects } = useGetProjectsQuery({
    filters: { status: "started" },
  });
  const suggestedProjects =
    startedProjects?.data.filter((project) => !isSelected(project)) ?? [];

  function addProjectToComparatorSelection(project: Project) {
    const projectName = project.content?.name;

    if (selectionCount === config.maxSelection) {
      return void toast(() => (
        <AlertRebrand variant="error">
          <AlertRebrandTitle className="lg:w-80">
            <XIcon className="text-error h-5 w-5" />
            Vous avez atteint le nombre maximum de {config.maxSelection}{" "}
            collectes dans le comparatif
          </AlertRebrandTitle>
        </AlertRebrand>
      ));
    }

    selectItem(project);

    customerTrackComparator(UI_PROJECTS_COMPARATOR_MODAL_PROJECT_ADDED, {
      project_id: project.id,
      project_name: project.content?.name,
    });

    toast(() => (
      <AlertRebrand variant="success">
        <AlertRebrandTitle>
          <CheckIcon className="text-success h-5 w-5 capitalize" />
          {projectName} ajouté au comparatif
        </AlertRebrandTitle>
      </AlertRebrand>
    ));
  }

  if (!suggestedProjects.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-6 border-t border-neutral-500 bg-neutral-100 p-4 lg:p-10">
      <h4 className="font-roobert text-black-primary text-lg font-semibold">
        D&apos;autres projets qui pourraient vous intéresser
      </h4>

      <section className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
        {suggestedProjects.slice(0, 3).map((project: Project) => (
          <ProjectCard
            key={project.id}
            project={project}
            className="min-w-[320px] md:min-w-[360px]"
          >
            <ProjectCardCover>
              <ButtonRebrand
                variant="ghost"
                onClick={() => addProjectToComparatorSelection(project)}
                className="absolute right-4 top-4 h-10 w-10 rounded-full bg-neutral-100"
              >
                <PlusIcon className="min-h-6 min-w-6 text-neutral-900" />
              </ButtonRebrand>
              <ProjectCardImage />
              <ProjectCardImageFallback />
              <ProjectCardStatusBadge />
              <ProjectCardClubAvatar />
            </ProjectCardCover>
            <ProjectCardContent>
              <ProjectCardHeader>
                <ProjectCardAvatar />
                <ProjectCardLabel />
              </ProjectCardHeader>
              <ProjectCardBadges />
              <ProjectCardDescription />
              <ProjectCardFooter>
                <ProjectCardAmount />
                <ProjectCardPercentage />
              </ProjectCardFooter>
            </ProjectCardContent>
          </ProjectCard>
        ))}
      </section>
    </div>
  );
}
